/* eslint-disable max-len */
/**
 * Berth Availability Slot Timeline Controller
 *
 * Manages interactive timeline visualizations for berth availability and booking allocation.
 * Renders timeline views showing berth occupancy slots, handles user interactions for berth
 * selection, displays tooltips with boat/booking details, and provides sidebar functionality
 * for detailed berth information.
 *
 * @description Key Features:
 * - Interactive timeline with drag/zoom capabilities
 * - Berth allocation and availability visualization
 * - Tooltip displays with booking and boat dimension details
 * - Optional sidebar for extended berth information
 * - Toggleable docking status indicators (colored dots)
 * - Integration with booking occupancy workflows
 *
 * @see app/views/marinas/berth_availability_slot_timeline/index.html.erb Standalone timeline view
 *   @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component
 * @see app/views/marinas/marina_occupancy_timeline/index.html.erb Marina occupancy timeline view
 *   @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component
 * @see app/views/marinas/booking_occupancy/index.html.erb Booking allocation interface
 *   @see app/views/marinas/booking_occupancy/partials/index/_berth_allocation.html.erb Berth selection component
 *     @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component (via turbo frame)
 * @see app/views/marinas/booking_occupancy/new.html.erb New booking berth selection
 *   @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component
 * @see app/views/marina/requests/show.html.erb Request details with berth info
 *   @see app/views/shared/marina/_berth_information.html.erb Berth information component
 *     @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component (via turbo frame)
 * @see app/views/marina/bookings/show.html.erb Booking details with berth info
 *   @see app/views/shared/marina/_berth_information.html.erb Berth information component
 *     @see app/views/marinas/berth_availability_slot_timeline/partials/_availability_slots_timeline.html.erb Timeline component (via turbo frame)
 *
 * @extends {Controller}
 */
/* eslint-enable max-len */
import { Controller } from "@hotwired/stimulus";
import { destroy } from "@rails/request.js";
import tippy from "tippy.js";
import { SidebarService } from "./shared/sidebar_service";

export default class extends Controller {
  static values = {
    items: Array,
    options: Object,
    groups: Array,
    autoResize: Boolean,
    start: String,
    end: String,
    enableSidebar: { type: Boolean, default: false },
    enableDockingStatus: { type: Boolean, default: false },
    contentTemplate: { type: String, default: "{{customerName}} │ {{boatName}} │ {{dateRange}}" },
    tooltipTemplate: { type: String, default: "{{boatDimensionsTitle}} | {{loaText}}: {{boatLength}}m | {{beamText}}: {{boatBeam}}m | {{draftText}}: {{boatDraft}}m" },
    noDimensionsText: String,
    unknownCustomerText: String,
    boatDimensionsText: String,
    loaText: String,
    beamText: String,
    draftText: String,
    failedToLoadTextValue: String,
    failedToRemoveSlotTextValue: String,
    networkErrorTextValue: String,
    locale: { type: String, default: "en" },
    selectionMode: { type: Boolean, default: false },
    templates: Array,
    selectedTemplate: { type: Number, default: 0 },
  };

  async connect() {
    try {
      this.setLoading(true);
      this.tooltipInstances = new Map(); // Track tooltip instances for cleanup

      if (this.selectionModeValue) {
        // Add selection mode class for CSS targeting
        this.element.classList.add("is-selection-mode");
        await this.initializeSelectionMode();
        // Sync initial selection from checked radio (if any)
        const checked = this.element.querySelector('input[type="radio"][name="slots_booking_occupancy_form[slot_template_raw]"]:checked');
        if (checked) {
          const idx = parseInt(checked.dataset.templateIndex, 10);
          if (!Number.isNaN(idx)) this.selectedTemplateValue = idx;
        }
        this.updateTimelineHighlight();
      } else {
        this.initializeDisplayMode();
      }

      this.setLoading(false);
    } catch (error) {
      console.error("Failed to initialize timeline:", error);
      this.setLoading(false);
      this.showError(this.failedToLoadTextValue || "Failed to load timeline visualization.");
    }
  }

  async initializeSelectionMode() {
    await this.initializeTimeline();
    this.initTooltips();
    this.generateRadioButtons();
    this.setupTemplateSelection();
    this.updateTimelineHighlight();

    // Debug timeline data for development
    if (import.meta.env.DEV) {
      this.debugTimelineData();
    }
  }

  async initializeDisplayMode() {
    await this.initializeTimeline();
    this.initTooltips();

    // Only add select event listener if sidebar is enabled
    if (this.enableSidebarValue) {
      this.timeline.on("select", (properties) => {
        if (properties.items && properties.items.length > 0) {
          const selectedItemId = properties.items[0];
          const selectedItem = this.itemsValue.find((item) => item.id === selectedItemId);

          if (selectedItem) {
            this.handleTimelineItemClick(selectedItem);
          }
        }
      });
    }
  }

  async initializeTimeline() {
    const { DataSet, Timeline } = await import("vis-timeline/standalone");
    await import("vis-timeline/styles/vis-timeline-graph2d.css");

    const container = this.element;
    this.itemsDataSet = new DataSet(this.processContent(this.itemsValue));
    const hasGroups = this.hasGroupsValue && this.groupsValue.length > 0;
    const processedGroups = hasGroups ? this.processGroupContent(this.groupsValue) : null;

    const options = this.prepareOptions();
    if (this.autoResizeValue) {
      this.setupResizing(options);
    }

    const timelineArgs = [container, this.itemsDataSet, options];
    if (hasGroups) {
      this.groupsDataSet = new DataSet(processedGroups);
      timelineArgs.splice(2, 0, this.groupsDataSet);
    }

    this.timeline = new Timeline(...timelineArgs);
  }

  disconnect() {
    if (this.boundResizeHandler) {
      window.removeEventListener("resize", this.boundResizeHandler);
    }

    // Cleanup all tooltip instances
    this.tooltipInstances.forEach((tooltip) => {
      tooltip.destroy();
    });
    this.tooltipInstances.clear();

    // Remove selection mode class
    this.element.classList.remove("is-selection-mode");

    if (this.timeline) {
      this.timeline.destroy();
      this.timeline = null;
    }

    if (this.itemsDataSet) {
      this.itemsDataSet = null;
    }

    if (this.groupsDataSet) {
      this.groupsDataSet = null;
    }
  }

  initTooltips() {
    // Lazy tooltip initialization - create tooltips only when items are hovered
    this.timeline.on("itemover", (properties) => {
      const itemId = properties.item;

      // Only create tooltip if it doesn't exist yet
      if (!this.tooltipInstances.has(itemId)) {
        const item = this.itemsValue.find((i) => i.id === itemId);
        // Get the processed item from the DataSet which has tooltipContent
        const processedItem = this.itemsDataSet.get(itemId);

        if (item && item.data) {
          // In selection mode, use pre-computed tooltipContent; in display mode, generate it
          const tooltipContent = this.selectionModeValue && processedItem
            && processedItem.tooltipContent
            ? processedItem.tooltipContent
            : this.getTooltipContent(item.data);

          // Find the DOM element for this timeline item - access the actual DOM element
          const timelineItem = this.timeline.itemSet?.items?.[itemId];
          if (!timelineItem) {
            console.warn(`Timeline item ${itemId} not found in itemSet`);
            return;
          }
          const itemElement = timelineItem.dom?.box || timelineItem.dom;

          if (itemElement && itemElement.nodeType === Node.ELEMENT_NODE) {
            const tippyInstance = tippy(itemElement, {
              content: tooltipContent,
              touch: false,
              arrow: true,
              theme: "small-font",
            });

            this.tooltipInstances.set(itemId, tippyInstance);

            // Manually show the tooltip since hover already started
            tippyInstance.show();
          }
        }
      }
    });
  }

  getTooltipContent(data) {
    const {
      boat_dimensions: boatDimensions,
      berth_dimensions: berthDimensions,
      customer_name: customerName,
      boat_name: boatName,
      dates,
    } = data;

    if (!boatDimensions) {
      return this.noDimensionsTextValue;
    }

    const {
      length_in_cm: boatLengthInCm,
      beam_in_cm: boatBeamInCm,
      draft_in_cm: boatDraftInCm,
    } = boatDimensions;
    const {
      length_in_cm: berthLengthInCm,
      beam_in_cm: berthBeamInCm,
      draft_in_cm: berthDraftInCm,
    } = berthDimensions || {};
    const { start_date: startDate, end_date: endDate } = dates;
    const { cmToMeters, formatDate } = this.constructor;

    // Prepare template variables for tooltip
    const templateData = {
      customerName: this.constructor.escapeHtml(customerName || this.unknownCustomerTextValue),
      boatName: this.constructor.escapeHtml(boatName || "N/A"),
      dateRange: `${formatDate(startDate, this.localeValue)} - ${formatDate(endDate, this.localeValue)}`,
      startDate: formatDate(startDate, this.localeValue),
      endDate: formatDate(endDate, this.localeValue),
      boatLength: cmToMeters(boatLengthInCm),
      boatBeam: cmToMeters(boatBeamInCm),
      boatDraft: cmToMeters(boatDraftInCm),
      berthLength: cmToMeters(berthLengthInCm),
      berthBeam: cmToMeters(berthBeamInCm),
      berthDraft: cmToMeters(berthDraftInCm),
      boatDimensionsTitle: this.boatDimensionsTextValue,
      loaText: this.loaTextValue,
      beamText: this.beamTextValue,
      draftText: this.draftTextValue,
      noDimensionsText: this.noDimensionsTextValue,
    };

    return this.constructor.applyTemplate(this.tooltipTemplateValue, templateData);
  }

  setupResizing(options) {
    this.headerElement = document.getElementById("berth-slots-timeline-header");

    if (!this.headerElement && import.meta.env.DEV) {
      console.warn("Berth timeline header element not found, auto-resize may not work correctly");
    }

    this.calculateAndSetSize(options);

    // Debounce resize calculations for better performance
    this.boundResizeHandler = this.debounce(() => {
      if (this.timeline) {
        this.calculateAndSetSize();
        this.timeline.redraw();
      }
    }, 250);

    window.addEventListener("resize", this.boundResizeHandler);
  }

  calculateAndSetSize(optionsObj = null) {
    if (!this.headerElement) return;

    const viewportHeight = window.innerHeight;
    const headerHeight = this.headerElement.offsetHeight;
    // Min height with padding
    const timelineHeight = Math.max(300, viewportHeight - headerHeight - 20);

    if (optionsObj) {
      // eslint-disable-next-line no-param-reassign
      optionsObj.height = `${timelineHeight}px`;
    } else if (this.timeline) {
      this.timeline.setOptions({ height: `${timelineHeight}px` });
    }
  }

  processContent(dataArray) {
    return dataArray.map((data) => {
      const dataCopy = { ...data };
      if (dataCopy.data) {
        if (this.selectionModeValue) {
          // In selection mode, all items are berths (free berths)
          const dockingClass = this.enableDockingStatusValue && dataCopy.data.docking_status
            ? `docking-status-${dataCopy.data.docking_status}`
            : "";
          const className = `timeline-item display-variant-free_berth ${dockingClass}`.trim();

          dataCopy.className = className;
        } else {
          // Display mode - use existing logic with display_variant
          const { display_variant: displayVariant, docking_status: dockingStatus } = dataCopy.data;
          const dockingClass = this.enableDockingStatusValue ? `docking-status-${dockingStatus}` : "";
          const className = `timeline-item display-variant-${displayVariant} ${dockingClass}`.trim();
          dataCopy.className = className;
        }

        if (this.selectionModeValue) {
          // For selection mode, use berth data to prepare content
          const contentData = this.prepareBerthContent(dataCopy.data);
          dataCopy.content = contentData.text;
          dataCopy.tooltipContent = contentData.title;
        } else {
          // For display mode, use existing boat booking content
          const contentData = this.prepareContent(dataCopy.data);
          if (typeof contentData === "object") {
            dataCopy.content = contentData.text;
            dataCopy.tooltipContent = contentData.title; // Store for tippy tooltip
          } else {
            dataCopy.content = contentData; // Fallback for backward compatibility
          }
        }
      } else if (typeof dataCopy.content === "string" && this.constructor.isHtmlString(dataCopy.content)) {
        const div = document.createElement("div");
        div.innerHTML = dataCopy.content.trim();
        dataCopy.content = div.firstChild;
      }
      return dataCopy;
    });
  }

  processGroupContent(groupsArray) {
    if (!this.selectionModeValue) {
      return this.processContent(groupsArray);
    }

    // For selection mode, embed radio buttons in group content
    return groupsArray.map((group) => {
      const groupCopy = { ...group };
      const template = this.templatesValue.find((t) => t.template_index === group.template_index);

      if (template) {
        // Create a container div with radio button and label
        const container = document.createElement("div");
        container.className = "flex items-center space-x-2";

        // Create radio button element
        const radioButton = document.createElement("input");
        radioButton.type = "radio";
        radioButton.name = "slots_booking_occupancy_form[slot_template_raw]";
        radioButton.value = JSON.stringify(template.template);
        radioButton.checked = template.is_default;
        radioButton.dataset.templateIndex = group.template_index;
        radioButton.className = "h-4 w-4 border-gray-300 text-teal-600 focus:ring-teal-600";

        // Create label element
        const label = document.createElement("span");
        label.textContent = group.display_label;
        label.className = "text-sm font-medium text-gray-700";

        container.appendChild(radioButton);
        container.appendChild(label);

        groupCopy.content = container;
      }

      return groupCopy;
    });
  }

  prepareContent(data) {
    const {
      customer_name: customerName,
      boat_name: boatName,
      boat_dimensions: boatDimensions,
      berth_dimensions: berthDimensions,
      dates,
    } = data;
    const {
      length_in_cm: boatLengthInCm,
      beam_in_cm: boatBeamInCm,
      draft_in_cm: boatDraftInCm,
    } = boatDimensions;
    const {
      length_in_cm: berthLengthInCm,
      beam_in_cm: berthBeamInCm,
      draft_in_cm: berthDraftInCm,
    } = berthDimensions || {};
    const { start_date: startDate, end_date: endDate } = dates;
    const { cmToMeters, formatDate } = this.constructor;

    // Prepare template variables
    const templateData = {
      customerName: this.constructor.escapeHtml(customerName || this.unknownCustomerTextValue),
      boatName: this.constructor.escapeHtml(boatName || "N/A"),
      dateRange: `${formatDate(startDate, this.localeValue)} - ${formatDate(endDate, this.localeValue)}`,
      startDate: formatDate(startDate, this.localeValue),
      endDate: formatDate(endDate, this.localeValue),
      boatLength: cmToMeters(boatLengthInCm),
      boatBeam: cmToMeters(boatBeamInCm),
      boatDraft: cmToMeters(boatDraftInCm),
      berthLength: cmToMeters(berthLengthInCm),
      berthBeam: cmToMeters(berthBeamInCm),
      berthDraft: cmToMeters(berthDraftInCm),
      boatDimensionsTitle: this.boatDimensionsTextValue,
      loaText: this.loaTextValue,
      beamText: this.beamTextValue,
      draftText: this.draftTextValue,
      noDimensionsText: this.noDimensionsTextValue,
    };

    // Apply templates with placeholder replacement
    const displayText = this.constructor.applyTemplate(this.contentTemplateValue, templateData);
    const tooltipText = this.constructor.applyTemplate(this.tooltipTemplateValue, templateData);

    // Return object with both display text and tooltip data
    return {
      text: displayText,
      title: tooltipText, // This will show as tooltip in vis-timeline
    };
  }

  static escapeHtml(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  static applyTemplate(template, data) {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => (data[key] !== undefined ? data[key] : match));
  }

  static cmToMeters(cm) {
    return cm ? (cm / 100).toFixed(2) : "N/A";
  }

  static formatDate(dateString, locale = "en") {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, { day: "numeric", month: "long" });
  }

  static isHtmlString(str) {
    return /<\/?[a-z][\s\S]*>/i.test(str);
  }

  // Stable comparator used for both rendering and alternation
  static compareItemsForSelection(a, b) {
    // choose one: berth_id first, else description, else id
    const keyA = String(a.data?.berth_id ?? a.data?.berth_description ?? a.id ?? "");
    const keyB = String(b.data?.berth_id ?? b.data?.berth_description ?? b.id ?? "");
    return keyA.localeCompare(keyB);
  }

  prepareOptions() {
    const options = { ...this.optionsValue };

    if (this.hasStartValue && this.startValue.trim() !== "") {
      options.start = new Date(this.startValue);
    }

    if (this.hasEndValue && this.endValue.trim() !== "") {
      options.end = new Date(this.endValue);
    }

    // Ensure vis stacks items in the same order we use for alternation
    if (this.selectionModeValue) {
      options.order = (a, b) => this.constructor.compareItemsForSelection(a, b);
    }

    // Note: onSelect removed - using timeline.on('select') event listener instead

    if (options.editable?.remove) {
      options.onRemove = async (item, callback) => {
        try {
          this.setLoading(true);
          const response = await destroy(`/berth_availability_slots/${item.id}`);

          if (response.ok) {
            callback(item);
            this.setLoading(false);
          } else {
            callback(null);
            this.setLoading(false);
            this.showError(this.failedToRemoveSlotTextValue || "Failed to remove the slot. Server responded with an error.");
          }
        } catch (error) {
          console.error("Error handling slot removal:", error);
          callback(null);
          this.setLoading(false);
          this.showError(this.networkErrorTextValue || "Network error when removing the slot.");
        }
      };
    }

    return options;
  }

  handleTimelineItemClick(item) {
    // Only execute if sidebar is enabled
    if (!this.enableSidebarValue) {
      return;
    }

    try {
      // Debug logging to verify item structure
      console.debug("Timeline item clicked:", item.id);

      // More robust date handling
      let date;
      if (item.start instanceof Date) {
        date = SidebarService.formatDate(item.start);
      } else if (typeof item.start === "string") {
        // Handle different date string formats
        [date] = item.start.split("T"); // Remove time part if present
      } else {
        console.error("Invalid date format in timeline item:", item.start);
        return;
      }

      // Verify we have a valid slot ID
      if (!item.id) {
        console.error("Timeline item missing ID:", item);
        return;
      }

      console.debug("Opening sidebar for slot:", item.id, "date:", date);

      // Open the sidebar with the slot information
      const success = SidebarService.openBerthSidebar({
        date,
        slotId: item.id,
        application: this.application, // Pass the application instance
        // berth_id will be deduced server-side from the slot
      });

      if (!success) {
        console.error("Failed to open sidebar for timeline item:", item);
      }
    } catch (error) {
      console.error("Error in handleTimelineItemClick:", error);
    }
  }

  showError(message) {
    const errorEvent = new CustomEvent("berth-timeline:error", {
      detail: { message },
      bubbles: true,
    });
    this.element.dispatchEvent(errorEvent);

    // Fallback to console error if no event listeners
    console.error(message);
  }

  setLoading(isLoading) {
    this.element.classList.toggle("is-loading", isLoading);

    const loadingEvent = new CustomEvent("berth-timeline:loading", {
      detail: { isLoading },
      bubbles: true,
    });
    this.element.dispatchEvent(loadingEvent);
  }

  // Debounce implementation to limit function calls
  // eslint-disable-next-line class-methods-use-this
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Selection mode methods
  // eslint-disable-next-line class-methods-use-this
  generateRadioButtons() {
    // Radio buttons are embedded in group content, so no separate container needed
    // This method is kept for compatibility but actual generation happens in processGroupContent
  }

  setupTemplateSelection() {
    // Add event listeners for radio button changes
    this.element.addEventListener("change", (event) => {
      if (event.target.type === "radio" && event.target.name === "slots_booking_occupancy_form[slot_template_raw]") {
        const templateIndex = parseInt(event.target.dataset.templateIndex, 10);
        this.selectedTemplateValue = templateIndex;
        this.updateTimelineHighlight();
      }
    });

    // Add click handlers for timeline items
    if (this.timeline) {
      this.timeline.on("select", (properties) => {
        if (properties.items && properties.items.length > 0) {
          const selectedItemId = properties.items[0];
          const selectedItem = this.itemsValue.find((item) => item.id === selectedItemId);

          if (selectedItem) {
            this.handleTemplateItemClick(selectedItem);
          }
        }
      });
    }
  }

  handleTemplateItemClick(item) {
    // Find which template this item belongs to and select it
    const templateIndex = item.group;
    this.selectTemplate(templateIndex);
  }

  selectTemplate(templateIndex) {
    this.selectedTemplateValue = templateIndex;

    // Update radio button selection
    const radios = this.element.querySelectorAll('input[type="radio"][name="slots_booking_occupancy_form[slot_template_raw]"]');
    radios.forEach((radio) => {
      const idx = parseInt(radio.dataset.templateIndex, 10);
      // eslint-disable-next-line no-param-reassign
      radio.checked = idx === templateIndex;
    });

    this.updateTimelineHighlight();
  }

  updateTimelineHighlight() {
    if (!this.timeline || !this.itemsDataSet) return;

    // Update items through DataSet to let vis-timeline handle rendering
    const updates = [];
    this.itemsValue.forEach((item) => {
      const currentItem = this.itemsDataSet.get(item.id);
      if (!currentItem) return;

      // Determine new className based on selection state
      const baseClasses = currentItem.className || "";
      const cleanedClasses = baseClasses.replace(/\btemplate-(selected|unselected)\b/g, "").trim();

      const selectionClass = item.group === this.selectedTemplateValue
        ? "template-selected"
        : "template-unselected";

      const newClassName = `${cleanedClasses} ${selectionClass}`.trim();

      // Only update if className changed
      if (currentItem.className !== newClassName) {
        updates.push({
          id: item.id,
          className: newClassName,
        });
      }
    });

    // Batch update the DataSet
    if (updates.length > 0) {
      this.itemsDataSet.update(updates);
    }
  }

  prepareBerthContent(data) {
    const {
      berth_description: berthDescription,
      berth_dimensions: berthDimensions,
      dates,
    } = data;

    const {
      length_in_cm: berthLengthInCm,
      beam_in_cm: berthBeamInCm,
      draft_in_cm: berthDraftInCm,
    } = berthDimensions || {};

    const { start_date: startDate, end_date: endDate } = dates;
    const { cmToMeters, formatDate } = this.constructor;

    // Prepare template variables for individual berth content
    const templateData = {
      berthDescription: this.constructor.escapeHtml(berthDescription || "N/A"),
      dateRange: `${formatDate(startDate, this.localeValue)} - ${formatDate(endDate, this.localeValue)}`,
      startDate: formatDate(startDate, this.localeValue),
      endDate: formatDate(endDate, this.localeValue),
      berthLength: cmToMeters(berthLengthInCm),
      berthBeam: cmToMeters(berthBeamInCm),
      berthDraft: cmToMeters(berthDraftInCm),
      loaText: this.loaTextValue,
      beamText: this.beamTextValue,
      draftText: this.draftTextValue,
    };

    // Show berth name and dimensions
    const berthContentTemplate = "{{berthDescription}} | {{dateRange}}";
    const berthTooltipTemplate = "{{berthDescription}} | {{loaText}}: {{berthLength}}m | {{beamText}}: {{berthBeam}}m | {{draftText}}: {{berthDraft}}m";

    // Apply templates with placeholder replacement
    const displayText = this.constructor.applyTemplate(berthContentTemplate, templateData);
    const tooltipText = this.constructor.applyTemplate(berthTooltipTemplate, templateData);
    // Return object with both display text and tooltip data
    return {
      text: displayText,
      title: tooltipText, // This will show as tooltip in vis-timeline
    };
  }

  debugTimelineData() {
    if (!import.meta.env.DEV) return;

    console.group("🔍 Timeline Data Debug");

    // Log timeline configuration
    console.log("📊 Timeline Options:", this.optionsValue);

    // Log group data
    console.log("👥 Groups:", this.groupsValue);

    // Log items data with overlap detection
    const items = this.itemsValue;
    console.log("📋 Items:", items);

    // Check for overlaps within each group
    const groupedItems = items.reduce((acc, item) => {
      if (!acc[item.group]) acc[item.group] = [];
      acc[item.group].push(item);
      return acc;
    }, {});

    Object.entries(groupedItems).forEach(([groupId, groupItems]) => {
      console.log(`🔍 Checking Group ${groupId}:`);

      // Sort items by start time
      const sortedItems = groupItems.sort((a, b) => new Date(a.start) - new Date(b.start));

      // Check for overlaps
      for (let i = 0; i < sortedItems.length - 1; i += 1) {
        const current = sortedItems[i];
        const next = sortedItems[i + 1];
        const currentEnd = new Date(current.end);
        const nextStart = new Date(next.start);

        console.log(`  📅 Item ${current.id}: ${current.start} → ${current.end}`);
        console.log(`  📅 Item ${next.id}: ${next.start} → ${next.end}`);

        if (currentEnd >= nextStart) {
          console.warn(`  ⚠️  OVERLAP DETECTED between ${current.id} and ${next.id}`);
          console.warn(`  ⚠️  Gap: ${(nextStart - currentEnd) / 1000}ms`);
        } else {
          const gapDays = (nextStart - currentEnd) / (1000 * 60 * 60 * 24);
          console.log(`  ✅ Gap: ${gapDays} days`);
        }
      }
    });

    console.groupEnd();
  }
}
